/**
 * SOLID Review
 * S — Strong. One concern: whose credits a balance is, and where it is kept — the account and the seated
 *     player — plus whose pushed balance to listen for.
 * O — Good. A new source of a balance is one more decision over `decideCreditsChanged`.
 * L — N/A — functions.
 * I — Good. Three decisions on the one ClientDecisions contract.
 * D — Strong. The session draft and the port schemas.
 */
import { decided, type IEntityDecision, type TDecisionResult } from '../shared';
import { draftOf, settleSession } from '../session/sessionDraft';
import type { IPropsClientChanges, IPropsClientInput } from '../../ports/schemas/ClientChanges';
import type { IEntityTableAck } from '../../ports/schemas/TableEvents';
import type { ICommandNothing } from '../../ports/schemas/TableMoves';
import type { ICommandCreditsChanged, ICommandBalanceRead, IPayloadAccountWatch } from '../../ports/schemas/PaymentMoves';

type TSession<D> = TDecisionResult<IEntityDecision<IPropsClientChanges, D>>;

const NOTHING: IPropsClientChanges = {};
const APPLIED: IEntityTableAck = { applied: true };
const NOT_APPLIED: IEntityTableAck = { applied: false };

/**
 * A balance for a player (frontend strict-ports plan, FE4c, 2026-10-04) — moved from AppRoot's push
 * handler and PaymentSuccessScreen's poll, which each wrote the auth store's credits
 * (`updateMatchCredits`) and the game store's seated player (`applyCreditsUpdated`). The account keeps
 * the balance when it is the account's or the seated player's; the seated player only when it is theirs
 * (its tier follows, in the store). A balance for anyone else changes nothing, whatever the server sent.
 */
export function decideCreditsChanged(input: IPropsClientInput, command: ICommandCreditsChanged): TSession<IEntityTableAck> {
  const { account, player } = input.session;
  const seated = player !== null && player.playerId === command.playerId;
  if (account.playerId !== command.playerId && !seated) return decided(NOTHING, NOT_APPLIED);
  const next = draftOf(input);
  next.session.account = { ...account, matchCredits: command.credits };
  if (seated) next.session.player = { ...player, matchCredits: command.credits };
  return decided(settleSession(input, next), APPLIED);
}

/** The signed-in account's own balance, read back after a purchase: the account's credits. */
export function decideBalanceRead(input: IPropsClientInput, command: ICommandBalanceRead): TSession<IEntityTableAck> {
  const { playerId } = input.session.account;
  if (playerId === null) return decided(NOTHING, NOT_APPLIED);
  const changed: ICommandCreditsChanged = { playerId, credits: command.credits };
  return decideCreditsChanged(input, changed);
}

/** Whose pushed balance to hear: the seated player's (AppRoot's subscription key), or nobody's. */
export function decidePlanAccountWatch(input: IPropsClientInput, _command: ICommandNothing): TSession<IPayloadAccountWatch> {
  const target: IPayloadAccountWatch = { playerId: input.session.player?.playerId ?? null };
  return decided(NOTHING, target);
}
